import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import selectors from "selectors";
import { Client } from "@twilio/conversations";

const useTwilioChat = ({ channel_sid }) => {
  const twilioToken = useSelector(selectors.getTwilioToken);
  const userId = useSelector(selectors.getUserId);
  const userName = useSelector(selectors.getUserName);
  const [client, setClient] = useState(null);
  const [error, setError] = useState();
  const [connecting, setIsConnecting] = useState(false);

  useEffect(() => {
    setIsConnecting(true);
    // Hook-based client for text-chat page. Saga-based client in twilio-chat.js serves global chat window - these surfaces don't render simultaneously.
    const conversationsClient = new Client(twilioToken);
    conversationsClient.on("stateChanged", state => {
      if (state === "initialized") {
        setClient(conversationsClient);
      }
      if (state === "failed") {
        setError("Failed to initialize Twilio client");
        setIsConnecting(false);
      }
    });
    return () => {
      conversationsClient.removeAllListeners();
      conversationsClient.shutdown();
    };
  }, [userId, twilioToken]);

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [members, setMembers] = useState([]);
  const [typingParticipants, setTypingParticipants] = useState([]);
  const [readReceipts, setReadReceipts] = useState({});
  const typingTimeoutsRef = useRef({});

  useEffect(() => {
    async function getMembers(conversation) {
      try {
        const participants = await conversation.getParticipants();
        setMembers(participants.map(p => p.identity));
      } catch (err) {
        console.error("could not get members:", err.message);
      }
    }

    async function restoreMessages(conversation) {
      try {
        const { items } = await conversation.getMessages();
        const restoredMessages = items.map(message => ({
          ...message.state
        }));
        setMessages(() => [...restoredMessages]);
      } catch (err) {
        console.error("could not restore messages:", err.message);
      }
    }

    async function connectToConversation(channel_sid, client) {
      try {
        const conv = await client.getConversationBySid(channel_sid);
        if (conv.status !== "joined") {
          await conv.join();
        }
        setConversation(conv);
        await Promise.all([restoreMessages(conv), getMembers(conv)]);
        setConnected(true);
      } catch (e) {
        setError(e.message);
      } finally {
        setIsConnecting(false);
      }
    }

    if (client && channel_sid) {
      connectToConversation(channel_sid, client);
    }
  }, [client, channel_sid]);

  useEffect(() => {
    async function addMessage(message) {
      setMessages(messages => [...messages, { ...message.state }]);
    }
    if (conversation) {
      console.debug("subscribing to chat channel");
      conversation.on("messageAdded", addMessage);
      conversation.on("participantJoined", member => {
        console.debug("member joined", member);
        setMembers(m => [...m, member.identity]);
      });
      conversation.on("participantLeft", member => {
        console.debug("member left", member);
        setMembers(m => m.filter(id => id !== member.identity));
      });
      conversation.on(
        "participantUpdated",
        ({ participant, updateReasons }) => {
          console.debug("participant updated", participant, updateReasons);
          if ((updateReasons.includes("lastReadMessageIndex") || updateReasons.includes("lastReadTimestamp")) && participant.state.identity !== userId) {
            setReadReceipts( {
                lastReadMessageIndex: participant.lastReadMessageIndex,
                lastReadTimestamp: participant.lastReadTimestamp
              }
            );
          }
        }
      );
      conversation.on("removed", () => {
        console.debug("channel removed");
        setConversation(null);
        setConnected(false);
      });

      conversation.on("typingStarted", participant => {
        const id = participant.identity;
        if (typingTimeoutsRef.current[id]) {
          clearTimeout(typingTimeoutsRef.current[id]);
        }
        setTypingParticipants(prev => {
          if (prev.includes(id)) return prev;
          return [...prev, id];
        });
        typingTimeoutsRef.current[id] = setTimeout(() => {
          setTypingParticipants(prev => prev.filter(p => p !== id));
          delete typingTimeoutsRef.current[id];
        }, 10000);
      });

      conversation.on("typingEnded", participant => {
        const id = participant.identity;
        if (typingTimeoutsRef.current[id]) {
          clearTimeout(typingTimeoutsRef.current[id]);
          delete typingTimeoutsRef.current[id];
        }
        setTypingParticipants(prev => prev.filter(p => p !== id));
      });

      return () => {
        console.debug("unsubscribing from chat channel");
        conversation.removeAllListeners();
        Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
        typingTimeoutsRef.current = {};
      };
    }
  }, [conversation]);

  const sendMessage = useCallback(
    msg => {
      if (conversation) {
        return conversation.sendMessage(msg, {
          authorName: userName,
          authorId: userId
        });
      } else {
        return Promise.reject("channel not defined");
      }
    },
    [conversation, userId, userName]
  );

  const leaveTwilioChat = useCallback(() => {
    if (conversation) {
      console.debug("leaving text chat");
      conversation.leave();
    }
  }, [conversation]);

  const sendTyping = useCallback(() => {
    if (conversation) {
      conversation
        .typing()
        .catch(err => console.error("Error sending typing signal:", err));
    }
  }, [conversation]);

  const setLastReceivedMessageAsRead = useCallback(async () => {
    if (conversation && messages.length > 0) {
      const lastReceivedMessage = [...messages]
        .reverse()
        .find(msg => msg.author !== userId);

      if (lastReceivedMessage?.index !== undefined) {
        try {
          await conversation.updateLastReadMessageIndex(lastReceivedMessage.index);
        } catch (err) {
          console.error("Error updating read horizon:", err);
        }
      }
    }
  }, [conversation, messages, userId]);

  return {
    messages,
    sendMessage,
    connected,
    connecting,
    error,
    leaveTwilioChat,
    members,
    typingParticipants,
    sendTyping,
    readReceipts,
    setLastReceivedMessageAsRead
  };
};

export default useTwilioChat;
